import { Middleware, UnknownAction } from '@reduxjs/toolkit'
import type { AuthState } from '../../types'
import type { Properties } from 'posthog-js'

interface RootState {
  auth: AuthState
}

interface AuthEventProperties extends Properties {
  action_type: string
  timestamp: string
  current_step: string
  previous_step?: string
  auth_method?: string
  error_message?: string
  available_methods?: string[]
  trigger_event?: string
}

// Helper function to track events
export const trackEvent = (
  shortEventName: string,
  state: AuthState,
  additionalProperties = {},
) => {
  const eventName = ('session_v2:' + shortEventName).toLowerCase()
  const properties = { ...createBaseProperties(state), ...additionalProperties }
  try {
    if (typeof window !== 'undefined' && window.posthog) {
      window.posthog.capture(eventName, properties)
    } else {
      // Fallback to console logging when PostHog isn't available
      console.log(`PostHog Event: ${eventName}`, properties)
    }
  } catch (error) {
    console.warn('PostHog tracking error:', error)
  }
}

// Helper to create base properties
const createBaseProperties = (
  state: AuthState,
): Partial<AuthEventProperties> => ({
  timestamp: new Date().toISOString(),
  current_step: state.step,
  auth_method: state?.signin?.authMethod?.type,
  available_methods: state?.signin?.userInfo?.authMethods,
  email: state?.signup?.email || state?.signin?.userInfo?.email,
  ...(state.error && { error_message: state.error }),
})

export const posthogMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action: UnknownAction) => {
    const prevState = store.getState()
    const result = next(action)
    const newState = store.getState()

    // Track step changes
    const stepChanged = prevState.auth.step !== newState.auth.step
    if (stepChanged) {
      const eventName = `step_${newState.auth.step}`
      const properties = {
        action_type: action.type,
        current_step: newState.auth.step,
        previous_step: prevState.auth.step,
      }

      trackEvent(eventName, newState.auth, properties)
    }

    if (action.type === 'auth/initializeState') {
      trackEvent('start', newState.auth)
    }

    return result
  }
